<template>
  <div class="product-images">
    <div
      class="image-container"
      @mousemove="zoom"
      @mouseleave="resetZoom"
      ref="imageContainer"
    >
      <img 
        :src="currentImage" 
        ref="image"
        @error="handleImageError" 
        v-if="currentImage"
      />
      <div v-else class="no-image">
        <span>No image available</span>
      </div>
    </div>

    <!-- Thumbnail Images -->
    <div class="thumbnails" v-if="displayedImages.length > 1">
      <div
        v-for="(img, index) in displayedImages"
        :key="index"
        @click="selectImage(index)"
        :class="{ active: currentIndex === index }"
        class="thumbnail-image"
      >
        <img :src="thumbnailFallbacks[index] || img" @error="handleThumbnailError($event, index)" />
      </div>
    </div>
    
    <!-- Debug info that can be removed -->
    <div v-if="debug" class="debug-info">
      <p>Images prop: {{ images.length }}</p>
      <p>localImages: {{ localImages.length }}</p>
      <p>Displayed Images: {{ displayedImages.length }}</p>
      <p>Current Index: {{ currentIndex }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductImages',
  props: {
    images: {
      type: Array,
      default: () => [],
    },
    thumbnails: {
      type: Array,
      default: () => [],
    },
    debug: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localImages: [], // We'll keep a local copy so we don't flicker
      currentIndex: 0,
      scale: 2, // Zoom scale factor
      imageFallbacks: {},
      thumbnailFallbacks: {}
    };
  },
  computed: {
    displayedImages() {
      // If the parent gave us new images, and they're non-empty, use them.
      // Otherwise, keep using localImages so we don’t flicker to “no image”.
      if (this.images && this.images.length > 0) {
        return this.images;
      }
      return this.localImages;
    },
    currentImage() {
      if (!this.displayedImages.length) return '';
      // If there's a fallback for the current index, use it
      if (this.imageFallbacks[this.currentIndex]) {
        return this.imageFallbacks[this.currentIndex];
      }
      return this.displayedImages[this.currentIndex];
    },
  },
  methods: {
    selectImage(index) {
      if (index >= 0 && index < this.displayedImages.length) {
        this.currentIndex = index;
      }
    },
    zoom(event) {
      if (!this.currentImage) return;
      
      const image = this.$refs.image;
      if (!image) return;
      
      const container = this.$refs.imageContainer;
      if (!container) return;

      const { left, top, width, height } = container.getBoundingClientRect();
      const x = ((event.clientX - left) / width) * 100;
      const y = ((event.clientY - top) / height) * 100;

      image.style.transformOrigin = `${x}% ${y}%`;
      image.style.transform = `scale(${this.scale})`;
    },
    resetZoom() {
      const image = this.$refs.image;
      if (image) {
        image.style.transform = 'scale(1)';
        image.style.transformOrigin = 'center center';
      }
    },
    handleImageError(event) {
      console.log('Main image failed to load:', event.target.src);
      // Set a fallback image for the main image
      this.imageFallbacks[this.currentIndex] =
        'https://placehold.co/200x200?text=No+Image';
      if (event.target) {
        event.target.src = this.imageFallbacks[this.currentIndex];
      }
    },
    handleThumbnailError(event, index) {
      console.log('Thumbnail failed to load:', index, event.target.src);
      // Set a fallback image for thumbnails
      this.thumbnailFallbacks[index] =
        'https://placehold.co/200x200?text=No+Image';
      if (event.target) {
        event.target.src = this.thumbnailFallbacks[index];
      }
    }
  },
  watch: {
    images: {
      immediate: true,
      handler(newImages) {
        // If we received a valid, non-empty array, store it in localImages
        if (newImages && newImages.length > 0) {
          this.localImages = [...newImages];
          this.currentIndex = 0;
          this.imageFallbacks = {};
          this.thumbnailFallbacks = {};
          console.log('Images updated to new non-empty array:', newImages.length);
        } else {
          console.log('Images prop is empty; retaining old localImages');
        }
      },
    },
  },
  mounted() {
    console.log('ProductImages mounted:', {
      images: this.images ? this.images.length : 0,
      thumbnails: this.thumbnails ? this.thumbnails.length : 0
    });
  }
};
</script>

<style scoped>
.product-images {
  text-align: center;
}

.image-container {
  width: 100%;
  max-width: 400px;
  height: 400px;
  margin: 0 auto;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.image-container img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.2s ease;
}

.no-image {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: #e9ecef;
  color: #6c757d;
  font-size: 16px;
}

.thumbnails {
  margin-top: 10px;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
}

.thumbnail-image {
  width: 60px;
  height: 60px;
  margin: 5px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.2s ease;
  overflow: hidden;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumbnail-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
}

.thumbnail-image.active,
.thumbnail-image:hover {
  border-color: #e67e22;
}

.debug-info {
  margin-top: 20px;
  text-align: left;
  font-size: 12px;
  color: #666;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 4px;
}
</style>
